import { useEffect } from 'react';
import { Monitor, Moon, Sun } from 'lucide-react';
import { Card, CardBody, CardHeader } from '@/components/card';
import { useTheme, type ThemePreference } from '@/lib/theme';
import { useMe, useUpdatePreferences } from '@/lib/auth';
import { useToast } from '@/components/toast';
import { cn } from '@/lib/utils';

const OPTIONS: Array<{
  value: ThemePreference;
  label: string;
  description: string;
  icon: typeof Sun;
  preview: string;
}> = [
  {
    value: 'light',
    label: 'Light',
    description: 'Bright surface, soft shadows',
    icon: Sun,
    preview: 'from-white to-slate-100',
  },
  {
    value: 'dark',
    label: 'Dark',
    description: 'Deep indigo, brighter accents',
    icon: Moon,
    preview: 'from-slate-900 to-slate-950',
  },
  {
    value: 'system',
    label: 'Match system',
    description: 'Follow your OS preference',
    icon: Monitor,
    preview: 'from-white via-slate-200 to-slate-900',
  },
];

export function AppearanceTab() {
  const { preference, setPreference } = useTheme();
  const me = useMe();
  const update = useUpdatePreferences();
  const toast = useToast();

  // Sync server preference into local state on first load so the picker
  // reflects what's stored on the user record.
  useEffect(() => {
    if (me.data?.theme_preference && me.data.theme_preference !== preference) {
      setPreference(me.data.theme_preference as ThemePreference);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me.data?.theme_preference]);

  async function pick(next: ThemePreference) {
    if (next === preference) return;
    setPreference(next);
    try {
      await update.mutateAsync({ theme_preference: next });
      toast.success(`Theme set to ${OPTIONS.find((o) => o.value === next)?.label.toLowerCase()}`);
    } catch {
      toast.error('Could not save preference');
    }
  }

  return (
    <Card>
      <CardHeader>
        <h3 className="text-sm font-semibold">Theme</h3>
        <p className="mt-0.5 text-xs text-muted-foreground">
          Light, dark, or follow your operating system.
        </p>
      </CardHeader>
      <CardBody>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          {OPTIONS.map((o) => {
            const active = preference === o.value;
            const Icon = o.icon;
            return (
              <button
                key={o.value}
                type="button"
                onClick={() => pick(o.value)}
                className={cn(
                  'group/opt flex flex-col gap-3 rounded-xl border bg-card p-4 text-left transition-all',
                  active
                    ? 'border-primary ring-2 ring-primary/20'
                    : 'border-border hover:border-primary/40',
                )}
              >
                <div
                  className={cn(
                    'h-20 rounded-lg bg-gradient-to-br shadow-inner ring-1 ring-border/50',
                    o.preview,
                  )}
                />
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <Icon className={cn('size-4', active ? 'text-primary' : 'text-muted-foreground')} />
                    <span className="text-sm font-medium">{o.label}</span>
                  </div>
                  {active ? (
                    <span className="text-[11px] font-medium uppercase tracking-wider text-primary">
                      Active
                    </span>
                  ) : null}
                </div>
                <p className="text-xs text-muted-foreground">{o.description}</p>
              </button>
            );
          })}
        </div>
      </CardBody>
    </Card>
  );
}
